import React, {useState, useEffect, useRef, forwardRef} from "react";
import { useAnnotation } from "../contexts/AnnotationContext";
import Duration from "./Duration";
import KeyChangeSection from "./KeyChangeSection";
import TempoChangeSection from "./TempoChangeSection";
import TimeSignatureChangeSection from "./TimeSignatureChangeSection";
import { useViewMode } from "../contexts/ViewModeContext";
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {calculateMaxBeats} from "../utils/subdivisionUtils";
import {isBeforeInTime} from "../utils/annotationUtils";
import ErrorValidationDialog from "./ErrorValidationDialog";

/**
 * TimeSignatureBatchEditor component for batch editing time signatures
 * Redesigned to match the TimeSignatureChangeSection UI
 */
const BarSettingsBatchEditor = React.forwardRef(({
  measures,
  durationAttributes,
  durationInitialValue,
  onDurationSubmit,
  onDirtyChange = () => {},
}, ref) => {
  const {
    initializeSheet: sheet,
    handleBatchTimeSignatureChanges, 
    keyChanges: contextKeyChanges, 
    tempoChanges: contextTempoChanges, 
    timeSignatures: contextTimeSignatures,
    handleBatchKeyChanges,
    handleBatchTempoChanges,
  } = useAnnotation();

  const [initialTimeSignatures, setInitialTimeSignatures] = useState([]);
  const [initialKeyChanges, setInitialKeyChanges] = useState([]);
  const [initialTempoChanges, setInitialTempoChanges] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errors, setErrors] = useState([]);

  // State for time signature changes
  const [timeSignatureChanges, setTimeSignatureChanges] = useState([]);
  // State for key changes
  const [keyChanges, setKeyChanges] = useState([]);
  // State for tempo changes
  const [tempoChanges, setTempoChanges] = useState([]);

  const durationRef = useRef();

  React.useImperativeHandle(ref, () => ({
    handleApply,
    discardChanges,
    computeTimeSignatureDifference,
    computeKeyDifference,
    computeTempoDifference,
    computeDurationDifference,
    getIsDirty: () => isDirty,
    getTimeSignature: () => timeSignatureChanges,
    getKey: () => keyChanges,
    getTempo: () => tempoChanges,
    getDurationValue: () => durationRef.current?.getValue(),
  }));

  // Check view mode
  const { isViewMode } = useViewMode();

  // Initialize from context time signatures
  useEffect(() => {
    if (!contextTimeSignatures || contextTimeSignatures.length === 0) return;
    
    // Convert context time signatures to batch editor format
    const editorChanges = contextTimeSignatures.map((ts, index) => ({
      id: index + 1,
      measure: ts.position.bar,
      timeSignature: ts.value
    }));

    setInitialTimeSignatures(editorChanges);
    setTimeSignatureChanges(editorChanges);

    if (contextKeyChanges.length > 0) {
      const kc = contextKeyChanges.map((kcObj, i) => ({
        id: i + 1,
        position: kcObj.position,
        value: kcObj.value
      }));
      setKeyChanges(kc);
      setInitialKeyChanges(kc);
    }

    if (contextTempoChanges.length > 0) {
      const tc = contextTempoChanges.map((tcObj, i) => ({
        id: i + 1,
        position: tcObj.position,
        value: tcObj.value
      }));
      setTempoChanges(tc);
      setInitialTempoChanges(tc);
    }
  
  }, [contextTimeSignatures, contextKeyChanges, contextTempoChanges]);

  const computeTimeSignatureDifference = () => {
    const initialTSString = JSON.stringify(initialTimeSignatures);
    const currentTSString = JSON.stringify(timeSignatureChanges);
    if (initialTSString === currentTSString) return null;
    return { original: initialTimeSignatures, current: timeSignatureChanges };
  }

  const computeKeyDifference = () => {
    const initialKCString = JSON.stringify(initialKeyChanges);
    const currentKCString = JSON.stringify(keyChanges);
    if (initialKCString === currentKCString) return null;
    return { original: initialKeyChanges, current: keyChanges };
  }

  const computeTempoDifference = () => {
    const initialTCString = JSON.stringify(initialTempoChanges);
    const currentTCString = JSON.stringify(tempoChanges);
    if (initialTCString === currentTCString) return null;
    return { original: initialTempoChanges, current: tempoChanges };
  }

  const computeDurationDifference = () => {
    const initDuration = durationInitialValue.duration_number_of_bars;
    const currentDuration = durationRef.current.getValue();
    if (initDuration == currentDuration) return null;
    else return { original: initDuration, current: currentDuration };
  }

  const checkUnAppliedChanges = () => {
    const initialTSString = JSON.stringify(initialTimeSignatures);
    const currentTSString = JSON.stringify(timeSignatureChanges);
    const initialKCString = JSON.stringify(initialKeyChanges);
    const currentKCString = JSON.stringify(keyChanges);
    const initialTCString = JSON.stringify(initialTempoChanges);
    const currentTCString = JSON.stringify(tempoChanges);

    const dirtyNow =
      initialTSString !== currentTSString ||
      initialKCString !== currentKCString ||
      initialTCString !== currentTCString ||
      durationRef.current.getValue() !== durationInitialValue.duration_number_of_bars;

    if (dirtyNow !== isDirty) {
      setIsDirty(dirtyNow);
      onDirtyChange(dirtyNow);
    }
  }

  useEffect(() => {
    checkUnAppliedChanges();
  }, [
    timeSignatureChanges,
    keyChanges,
    tempoChanges,
    initialTimeSignatures,
    initialKeyChanges,
    initialTempoChanges,
  ]);

  const discardChanges = () => {
    setTempoChanges(initialTempoChanges);
    setTimeSignatureChanges(initialTimeSignatures);
    setKeyChanges(initialKeyChanges);
    durationRef.current.discardChanges();
  }

  const checkBarValid = () => {
    const maxBar = durationRef.current?.getValue() ?? 0;
    // All bar numbers in positions are valid
    for (let p of timeSignatureChanges) {
      if (p.measure < 1 || p.measure > maxBar) {
        return false;
      }
    }
    for (let p of keyChanges) {
      if (p.position.bar < 1 || p.position.bar > maxBar) {
        return false;
      }
    }
    for (let p of tempoChanges) {
      if (p.position.bar < 1 || p.position.bar > maxBar) {
        return false;
      }
    }
    return true;
  }

  const checkBeatValid = (timeSignatureRanges) => {
    // All beat numbers in positions are valid
    for (let p of keyChanges) {
      let timeSignature = "";
      for (let rng of timeSignatureRanges) {
        if (rng.start <= p.position.bar && p.position.bar <= rng.end) {
          timeSignature = rng.timeSignature;
        }
      }
      let keyBeat = p.position.beat;
      if (timeSignature === "" || keyBeat < 1 || keyBeat > calculateMaxBeats(timeSignature)) {
        return false;
      }
    }
    for (let p of tempoChanges) {
      let timeSignature = "";
      for (let rng of timeSignatureRanges) {
        if (rng.start <= p.position.bar && p.position.bar <= rng.end) {
          timeSignature = rng.timeSignature;
        }
      }
      let tempoBeat = p.position.beat;
      if (timeSignature === "" || tempoBeat < 1 || tempoBeat > calculateMaxBeats(timeSignature)) {
        return false;
      }
    }
    return true;
  }

  const checkSubdivisionValid = (timeSignatureRanges) => {
    // All subdivision numbers in positions are valid
    for (let p of keyChanges) {
      let timeSignature = "";
      for (let rng of timeSignatureRanges) {
        if (rng.start <= p.position.bar && p.position.bar <= rng.end) {
          timeSignature = rng.timeSignature;
        }
      }
      let keySubdivision = p.position.subdivision;
      const denom = parseInt(timeSignature.split("/")[1]);
      let subdivisionNumber = 16 / denom;
      if (timeSignature === "" || keySubdivision < 1 || keySubdivision > subdivisionNumber) {
        return false;
      }
    }
    for (let p of tempoChanges) {
      let timeSignature = "";
      for (let rng of timeSignatureRanges) {
        if (rng.start <= p.position.bar && p.position.bar <= rng.end) {
          timeSignature = rng.timeSignature;
        }
      }
      let tempoSubdivision = p.position.subdivision;
      const denom = parseInt(timeSignature.split("/")[1]);
      let subdivisionNumber = 16 / denom;
      if (timeSignature === "" || tempoSubdivision < 1 || tempoSubdivision > subdivisionNumber) {
        return false;
      }
    }
    return true;
  }

  const checkInSequentialOrder = () => {
    for (let i = 0; i < timeSignatureChanges.length - 1; i++) {
      if (!isBeforeInTime(timeSignatureChanges[i].measure, 0, 0, timeSignatureChanges[i + 1].measure, 0, 0)) {
        return false;
      }
    }
  
    for (let i = 0; i < keyChanges.length - 1; i++) {
      if (!isBeforeInTime(keyChanges[i].position.bar, keyChanges[i].position.beat, keyChanges[i].position.subdivision,
                          keyChanges[i + 1].position.bar, keyChanges[i + 1].position.beat, keyChanges[i + 1].position.subdivision)) {
        return false;
      }
    }
    for (let i = 0; i < tempoChanges.length - 1; i++) {
      if (!isBeforeInTime(tempoChanges[i].position.bar, tempoChanges[i].position.beat, tempoChanges[i].position.subdivision,
                          tempoChanges[i + 1].position.bar, tempoChanges[i + 1].position.beat, tempoChanges[i + 1].position.subdivision)) {
        return false;
      }
    }
    return true;
  }

  const checkNoDuplicate = () => {
    for (let i = 0; i < timeSignatureChanges.length; i++) {
      for (let j = i + 1; j < timeSignatureChanges.length; j++) {
        if (timeSignatureChanges[i].measure === timeSignatureChanges[j].measure) {
          return false;
        }
      }
    }
    for (let i = 0; i < keyChanges.length - 1; i++) {
      for (let j = i + 1; j < keyChanges.length; j++) {
        if (keyChanges[i].position.bar === keyChanges[j].position.bar &&
            keyChanges[i].position.beat === keyChanges[j].position.beat &&
            keyChanges[i].position.subdivision === keyChanges[j].position.subdivision) {
          return false;
        }
      }
    }
    for (let i = 0; i < tempoChanges.length - 1; i++) {
      for (let j = i + 1; j < tempoChanges.length; j++) {
        if (tempoChanges[i].position.bar === tempoChanges[j].position.bar &&
            tempoChanges[i].position.beat === tempoChanges[j].position.beat &&
            tempoChanges[i].position.subdivision === tempoChanges[j].position.subdivision) {
          return false;
        }
      }
    }
    return true;
  }

  const validityCheck = (timeSignatureRanges) => {
    // time signature is like {id, measure -> int, timeSignature -> str}
    // key is like {position.bar|beat|subdivision -> int}
    // tempo is like {position.bar|beat|subdivision -> int}
    let errors = [];
    if (!checkBarValid()) errors.push("Bar numbers in positions for Time Signature, Key, and Tempo lists must be a positive int between 1 and Duration - Number of Bars.\n 节拍、曲调和曲速列表中所有“位置”的小节编号必须为介于1和“歌曲时长-小节数”之间的正整数。");
    if (!checkBeatValid(timeSignatureRanges)) errors.push("Some beat numbers in positions for Key, and Tempo lists are invalid.\n 在曲调列表和曲速列表中，一些位置信息含有的拍子数值是错误的。");
    if (!checkSubdivisionValid(timeSignatureRanges)) errors.push("Some subdivision numbers in positions for Key, and Tempo lists are invalid.\n 在曲调列表和曲速列表中，一些位置信息含有的亚拍数值是错误的。");
    if (!checkInSequentialOrder()) errors.push("TimeSignature, Key, or Tempo list is out of order!");
    if (!checkNoDuplicate()) errors.push("Some events in the Time Signature, Key, and Tempo lists do not follow an increasing order in position, or there are events with identical (duplicate) positions in the same list.\n 在拍号、曲调和速度列表中，某些事件的位置未按递增顺序排列，或在同一列表中存在位置相同（重复）的情况。");
    return errors;
  }

  // Apply time signature changes
  const handleApply = () => {
    if (timeSignatureChanges.length === 0) {
      setErrors(['Please add at least one time signature change.']);
      setShowErrorDialog(true);
      return;
    }

    const numBarsEntered = durationRef.current?.getValue();
    if (numBarsEntered == null || !Number.isInteger(numBarsEntered) || numBarsEntered <= 0) {
      // this covers “doesn’t exist” (raw == null) and “isn’t a positive integer”
      setErrors(["Please enter a positive integer for Duration – Number of Bars"]);
      setShowErrorDialog(true);
      return;
    }
    
    // Convert to ranges format expected by the service
    const ranges = [];
    
    // Sort first by measure to ensure proper ranges
    const sortedChanges = [...timeSignatureChanges].sort((a, b) => parseInt(a.measure) - parseInt(b.measure));
    
    // Check for duplicate measures - we don't need two time signatures at the same measure
    const uniqueMeasures = new Set();
    const filteredChanges = sortedChanges.filter(change => {
      if (uniqueMeasures.has(change.measure)) {
        return false; // Skip duplicate measure
      }
      uniqueMeasures.add(change.measure);
      return true;
    });
    
    // Optimize time signature changes - remove unnecessary ones
    const optimizedChanges = [];
    let currentTimeSignature = null;
    
    for (const change of filteredChanges) {
      // First time signature is always necessary
      if (currentTimeSignature === null) {
        optimizedChanges.push(change);
        currentTimeSignature = change.timeSignature;
      } 
      // Only add a new time signature if it's different from the current one
      else if (change.timeSignature !== currentTimeSignature) {
        optimizedChanges.push(change);
        currentTimeSignature = change.timeSignature;
      }
    }
    
    // Create ranges from optimized changes
    for (let i = 0; i < optimizedChanges.length; i++) {
      const change = optimizedChanges[i];
      const nextChange = i < optimizedChanges.length - 1 ? optimizedChanges[i + 1] : null;
      
      const range = {
        start: parseInt(change.measure),
        end: nextChange ? parseInt(nextChange.measure) - 1 : durationRef.current?.getValue() ?? measures,
        timeSignature: change.timeSignature
      };
      
      ranges.push(range);
    }

    const validityCheckErrors = validityCheck(ranges);
    if (validityCheckErrors.length > 0) {
      setErrors(validityCheckErrors);
      setShowErrorDialog(true);
      return;
    } else {
      setErrors([]);
    }

    // TODO:: NOTE THAT AFTER HANDLERS ARE TRIGGERED, THE APPLY CHANGES BUTTON BECOMES INACTIVE
    //  BECAUSE THE ISDIRTY CHECK ALREADY RAN AGAIN AND THE NEWER CHANGE ALREADY WENT INTO THE CONTEXT AND DEEMED AS NO CHANGE
    //  WE SHOULD VALIDATE EVERYTHING BEFORE CALLING HANDLE CHANGES!!!!!!!!!

    // Apply tempo changes
    var result = handleBatchTempoChanges(tempoChanges);
    if (result && !result.success) {
      setErrors([result.error]);
      return;
    }
  
    // Apply key changes
    result = handleBatchKeyChanges(keyChanges);
    if (result && !result.success) {
      setErrors([result.error]);
      return;
    }

    // Apply timesignature changes
    result = handleBatchTimeSignatureChanges(ranges);
    if (result && !result.success) {
      setErrors([result.error]);
      return;
    }

    // Call Duration's onSubmit
    if (durationRef.current) {
      durationRef.current.submit();
    }
    
    // Apply was successful
    setErrors([]);
    setIsDirty(false);
    onDirtyChange(false);
    // Let the parent component know we're done
    // Note: The batch editor likely doesn't control its own visibility
    // so we may not need this line at all
  };
  
  return (
    <div className="mb-6">
      <div className="p-4">
        {/* CSS Grid Layout: 3 columns for Time Signature, Key, Tempo */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <TimeSignatureChangeSection timeSignatureChanges={timeSignatureChanges} onChange={setTimeSignatureChanges} measures={measures}/>
          <KeyChangeSection keyChanges={keyChanges} onChange={setKeyChanges} />
          <TempoChangeSection tempoChanges={tempoChanges} onChange={setTempoChanges} />
        </div>
        
        {/* Duration Section - Full width below the grid */}
        <div className="mb-6">
          <Duration
            ref={durationRef}
            attributes={durationAttributes}
            initialValues={durationInitialValue}
            onSubmit={onDurationSubmit}
            checkUnAppliedChanges={checkUnAppliedChanges}
          />
        </div>
        
        {/* Centered Apply button */}
        <div className="flex justify-center mt-4">
          {!isViewMode && <Button
            variant="contained"
            sx={{
              backgroundColor: "black",
              color: "white",
              "&:hover": { backgroundColor: "#333" },
            }}
            onClick={handleApply}
            className={"apply-button"}
            disabled={isViewMode || !isDirty}
          >
            Apply Changes (确认修改)
          </Button>}
        </div>
        
        {/* Error message */}

        {/*{errors && (
          <div className="mt-3 text-red-500 text-sm text-center">{errors}</div>
        )}*/}

        <ErrorValidationDialog
          open={showErrorDialog && errors.length > 0}
          onClose={() => setShowErrorDialog(false)}
          errors={errors}
        />
      </div>
    </div>
  );
});

export default BarSettingsBatchEditor;
